import React, { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import useClient from 'api/client';
import checkExistingShareObject from 'api/ShareObject/checkExistingShareObject';
import createShareObjectMutation from 'api/ShareObject/createShareObjectMutation';
import errorIcon from 'assets/icons/error-info.svg';
import AutocompleteTooltip, {
    Option,
} from 'components/Inputs/InputsWithTooltip/AutocompleteTooltip';

import {
    AwsEnvironment,
    DataType,
    EnvironmentsCheckboxes,
    Role,
    SelectedEnvironment,
    ShareObjectInfo,
    ShareRequestItem,
} from 'types/Share';

import { NButton, NCheckbox } from '@innersource-nn/novoui-react';
import useResourceEnvironments from 'views/Shares/hooks/useResourceEnvironments';
import { UserRoleForDataset } from 'types/Dataset';
import queryRole from 'api/Dataset/queryRole';

const emptyCheckboxes = {
    prodAccount: {
        checked: false,
        stage: 'PROD',
        account: 'prodAccount',
        label: 'Share with Production account',
    },
    valAccount: {
        checked: false,
        stage: 'VAL',
        account: 'valAccount',
        label: 'Share with Validation account',
    },
    testAccount: {
        checked: false,
        stage: 'TEST',
        account: 'testAccount',
        label: 'Share with Testing account',
    },
    devAccount: {
        checked: false,
        stage: 'DEV',
        account: 'devAccount',
        label: 'Share with Development account',
    },
};

interface ShareRequestProps {
    dataType?: DataType;
    resourceUri: string;
    handleShareRequest: (shareRequestItem: ShareRequestItem) => void;
    buttonText?: string;
}

const SelectEnvironmentStep = ({
    dataType = DataType['dataset'],
    resourceUri,
    handleShareRequest,
    buttonText,
}: ShareRequestProps) => {
    const client = useClient();
    const {
        environmentError,
        environmentOptions,
        loading,
        resource,
        setEnvironmentError,
        roleOptions,
        setRoleOptions,
        fetchRoles,
    } = useResourceEnvironments({ dataType, resourceUri });
    const [checkboxes, setCheckboxes] =
        useState<EnvironmentsCheckboxes>(emptyCheckboxes);
    const [currentEnv, setCurrentEnv] = useState<SelectedEnvironment>(
        {} as SelectedEnvironment,
    );
    const [currentRole, setCurrentRole] = useState<Role>({} as Role);
    const [generalError, setGeneralError] = useState<string | null>(null);
    const [loadingStep, setLoadingStep] = useState<boolean>(false);

    function handleSelectEnvironment(
        selectedEnvironment: SelectedEnvironment | Option,
    ) {
        setEnvironmentError(null);
        setRoleOptions([]);

        if (selectedEnvironment !== null) {
            setCurrentEnv(selectedEnvironment as SelectedEnvironment);
            if (selectedEnvironment.name) {
                fetchRoles(selectedEnvironment.name);
            }
        } else {
            setCurrentEnv({} as SelectedEnvironment);
        }
        setCurrentRole({} as Role);
    }

    const checkSubmit = async () => {
        try {
            setLoadingStep(true);
            const checkedValues = Object.values(checkboxes).filter(
                (checkbox) => checkbox.checked,
            );

            const currentAccounts: string[] = checkedValues.map(
                (checkbox: AwsEnvironment) => checkbox.account,
            );
            const currentStages: string[] = checkedValues.map(
                (checkbox: AwsEnvironment) => checkbox.stage,
            );
            const response = await client.query(
                checkExistingShareObject({
                    datasetUri:
                        dataType === 'dataset'
                            ? resourceUri
                            : resource.datasetUri,
                    input: {
                        principalId: currentEnv.name,
                        principalType: 'Environment',
                        currentRole: currentRole.value,
                        currentAccounts: currentAccounts.map(
                            (account: string) => account,
                        ),
                        currentStages: currentStages,
                    },
                }),
            );
            if (!response.errors) {
                const data = response.data.checkExistingShareObject;
                if (!data) {
                    await submit(currentAccounts, currentStages);
                } else {
                    handleShareRequest({
                        name: resource.name,
                        datasetUri: resource.datasetUri,
                        shareUri: data.shareUri,
                    });
                }
            } else {
                setGeneralError(
                    'Could not verify if another share object exists',
                );
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingStep(false);
        }
    };

    const submit = async (
        currentAccounts: string[],
        currentStages: string[],
    ) => {
        try {
            const shareObjectInfo: ShareObjectInfo = {
                dataset: {
                    datasetUri: resource.datasetUri,
                    itemType: '',
                    itemUri: '',
                },
                table: {
                    datasetUri: resource.datasetUri,
                    itemType: 'DatasetTable',
                    itemUri: resource.tableUri || '',
                },
                location: {
                    datasetUri: resource.datasetUri,
                    itemType: 'DatasetStorageLocation',
                    itemUri: resource.locationUri || '',
                },
            };

            const userRoleResponse = await client.query(
                queryRole(resource.datasetUri),
            );

            const datasetRole =
                userRoleResponse.data.getDataset.userRoleForDataset;
            // Skip sending email if requester is dataset owner or steward
            const skipShareEmail = [
                UserRoleForDataset.BusinessOwner,
                UserRoleForDataset.DataSteward,
            ].includes(datasetRole);

            const response = await client.mutate(
                createShareObjectMutation({
                    datasetUri: shareObjectInfo[dataType].datasetUri,
                    itemUri: shareObjectInfo[dataType].itemUri,
                    itemType: shareObjectInfo[dataType].itemType,
                    input: {
                        principalId: currentEnv.name,
                        principalType: 'Environment',
                        currentRole: currentRole.value,
                        currentAccounts,
                        currentStages,
                        skipShareEmail,
                    },
                }),
            );

            if (!response.errors) {
                if (dataType === 'dataset') {
                    handleShareRequest({
                        name: resource.name,
                        datasetUri: resource.datasetUri,
                        shareUri: response.data.createShareObject.shareUri,
                    });
                } else if (dataType === 'table') {
                    handleShareRequest({
                        name: resource.name,
                        datasetUri: resource.datasetUri,
                        shareUri: response.data.createShareObject.shareUri,
                        tableUri: resourceUri,
                    });
                }
            } else {
                setGeneralError(
                    `Creation of share object failed. ${response.errors[0]?.message}`,
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    let roleEmpty: boolean = Object.values(currentEnv).length === 0;

    let buttonDisabled: boolean = !(
        Object.values(checkboxes).some((checkbox) => checkbox.checked) &&
        Object.values(currentEnv).length !== 0 &&
        Object.values(currentRole).length !== 0
    );

    return (
        <>
            {loading ? (
                <CircularProgress />
            ) : (
                <form>
                    <div className="grid gap-4 mt-16">
                        <AutocompleteTooltip
                            label="Select environment"
                            placeholder="Choose environment"
                            tooltip="Select the environment you want the dataset to be accessible through."
                            items={environmentOptions}
                            handleSelected={handleSelectEnvironment}
                            error={environmentError}
                        />
                        <AutocompleteTooltip
                            label="Select target group"
                            placeholder="Choose target group"
                            tooltip="Specify the target group within the selected environment that you want to have access to the content of dataset."
                            items={roleOptions}
                            handleSelected={(selectedGroup: Role | Option) => {
                                if (selectedGroup !== null) {
                                    setCurrentRole(selectedGroup as Role);
                                } else {
                                    setCurrentRole({} as Role);
                                }
                            }}
                        />
                        {Object.entries(checkboxes).map(
                            ([key, values]: [string, AwsEnvironment]) => (
                                <NCheckbox
                                    key={key}
                                    checkboxSize="large"
                                    name={values.account}
                                    disabled={
                                        !currentEnv[
                                            key as keyof SelectedEnvironment
                                        ] || roleEmpty
                                    }
                                    onChange={(
                                        event: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const { checked } = event.target;
                                        setCheckboxes({
                                            ...checkboxes,
                                            [key]: {
                                                ...values,
                                                checked,
                                            },
                                        });
                                    }}
                                >
                                    {values.label}
                                </NCheckbox>
                            ),
                        )}
                    </div>
                    {generalError && (
                        <div className="flex ml-4">
                            <img src={errorIcon} alt="error information icon" />
                            <span className="text-lava-red-700 text-xs font-light">
                                {generalError}
                            </span>
                        </div>
                    )}
                    <NButton
                        className="mt-16 bg-sea-blue-600 font-light"
                        disabled={buttonDisabled}
                        isLoading={loadingStep}
                        variant={buttonDisabled ? 'fill' : undefined}
                        square
                        onClick={checkSubmit}
                    >
                        {buttonText ?? 'Next step'}
                    </NButton>
                </form>
            )}
        </>
    );
};

export default SelectEnvironmentStep;
